import React, { useCallback, useEffect, useState } from 'react';
import { Clock, MessageCircle, Phone, UserCheck, X } from 'lucide-react';
import api from '../services/api';
import { apriWhatsApp } from '../utils/recapiti';
import { annunciaAgendaCambiata } from '../utils/annunci';
import '../styles/Ritardi.css';
import PilaAvvisi from './PilaAvvisi';
import { notificaErrore, notificaAvviso, messaggioDa } from '../utils/notifiche';

/**
 * RitardiNotifier — i pazienti che dovevano essere qui e non risultano
 * arrivati.
 *
 * PERCHE' ARRIVA ADDOSSO E NON SI VA A CERCARE
 * L'agenda mostra la giornata, e in mezzo a quaranta slot uno scaduto da
 * sei minuti non salta all'occhio. Chi sta alla reception sta facendo
 * altro — accoglie, incassa, risponde al telefono — e il ritardo va
 * portato a lei.
 *
 * SPARISCE DA SOLO, e questo e' il punto. La riga se ne va nel momento
 * in cui qualcuno segna che il paziente e' arrivato: non c'e' niente da
 * archiviare, da spuntare o da ricordarsi. Un avviso che chiede un gesto
 * in piu' per chiudersi viene chiuso senza leggerlo.
 *
 * NON SI PUO' CHIUDERE «PER OGGI»: puo' essere nascosto per dieci
 * minuti, e poi torna. Un paziente che non e' arrivato resta un fatto
 * anche se in questo momento non e' comodo guardarlo.
 *
 * COSA OFFRE: scrivere su WhatsApp, chiamare, o dichiarare che e' gia'
 * in sala d'attesa. Le prime due sono il lavoro che la segreteria
 * farebbe comunque; la terza e' la stessa cosa del pulsante «Arrivato»
 * in agenda, portata dove serve.
 */

const OGNI = 60 * 1000;          // si ricontrolla ogni minuto
const RINVIO = 10 * 60 * 1000;   // «piu' tardi» dura dieci minuti

const nomeDi = (r) => (r.paz_cognome ? `${r.paz_cognome} ${r.paz_nome}` : r.paziente_nome) || 'Paziente';

const oraDi = (s) => {
    /* `inizio` e' scritto in UTC: a schermo va l'ora della clinica. */
    const d = new Date(String(s).endsWith('Z') ? s : s + 'Z');
    return isNaN(d) ? '' : d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const RitardiNotifier = () => {
    const [ritardi, setRitardi] = useState([]);
    const [rinviatiFino, setRinviatiFino] = useState({});   // id -> istante
    const [inCorso, setInCorso] = useState(null);

    const controlla = useCallback(async () => {
        try {
            const r = await api.get('/agenda/ritardi');
            setRitardi(r.data?.data?.ritardi || []);
        } catch (e) {
            /* Silenzio: un errore qui non deve piantare un riquadro rosso
               in mezzo alla giornata di chi sta lavorando. */
        }
    }, []);

    useEffect(() => {
        controlla();
        const t = setInterval(controlla, OGNI);
        return () => clearInterval(t);
    }, [controlla]);

    const segnaArrivato = async (r) => {
        setInCorso(r.id);
        try {
            await api.post(`/agenda/${r.id}/presenza`, { presenza: 'presente' });
            /* Si rilegge invece di togliere la riga a mano: se nel
               frattempo ne e' scaduto un altro, si vede subito. */
            await controlla();
            /* E l'AGENDA deve saperlo: il pallino giallo sul paziente e'
               il punto di questo pulsante, ed e' l'unica cosa che resta
               quando l'avviso sparisce. Senza, il pallino compariva solo
               ricaricando la pagina - e chi ha premuto non vedeva
               succedere niente dove si aspettava di vederlo. */
            annunciaAgendaCambiata();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco a segnare la presenza'));
        } finally {
            setInCorso(null);
        }
    };

    const rinvia = (id) =>
        setRinviatiFino((p) => ({ ...p, [id]: Date.now() + RINVIO }));

    const daMostrare = ritardi.filter((r) => (rinviatiFino[r.id] || 0) < Date.now());
    if (!daMostrare.length) return null;

    return (
        <PilaAvvisi riassunto={(n) => `${n} pazienti in ritardo`} voci={daMostrare.map((r) => (
                <div className="rit-avviso" key={r.id}>
                    <button className="rit-rinvia" onClick={() => rinvia(r.id)}
                            title="Nascondi per dieci minuti">
                        <X size={14} aria-hidden="true" />
                    </button>

                    <div className="rit-testa">
                        <Clock size={15} aria-hidden="true" />
                        <span className="rit-minuti">{r.minuti_ritardo} minuti di ritardo</span>
                    </div>

                    <div className="rit-nome">{nomeDi(r)}</div>

                    <div className="rit-dettagli">
                        <span>Appuntamento delle <b>{oraDi(r.inizio)}</b></span>
                        {r.agenda_nome && <span>{r.agenda_nome}</span>}
                        {r.stanza_nome && <span>{r.stanza_nome}</span>}
                    </div>
                    {r.prestazioni && <div className="rit-prestazioni">{r.prestazioni}</div>}

                    <div className="rit-azioni">
                        <button className="rit-whatsapp"
                                disabled={!r.telefono}
                                title={r.telefono ? 'Scrivi su WhatsApp' : 'Nessun numero in anagrafica'}
                                onClick={() => {
                                    const problema = apriWhatsApp(r.telefono);
                                    if (problema) notificaAvviso(problema);
                                }}>
                            <MessageCircle size={15} aria-hidden="true" /> WhatsApp
                        </button>

                        {/* `tel:` apre il telefono del sistema. Il giorno che ci
                            sara' il centralino, questo tasto parte da qui. */}
                        <a className={`rit-telefono${r.telefono ? '' : ' rit-spento'}`}
                           href={r.telefono ? `tel:${String(r.telefono).replace(/\s/g, '')}` : undefined}
                           title={r.telefono ? 'Chiama' : 'Nessun numero in anagrafica'}>
                            <Phone size={15} aria-hidden="true" /> Chiama
                        </a>

                        <button className="rit-presente"
                                disabled={inCorso === r.id}
                                onClick={() => segnaArrivato(r)}>
                            <UserCheck size={15} aria-hidden="true" />
                            {inCorso === r.id ? 'Segno…' : 'È in sala d’attesa'}
                        </button>
                    </div>
                </div>
            ))} />
    );
};

export default RitardiNotifier;
